import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import type { SurveyExpiration } from '@/shared/types/nps';
import { createSurveySchema, toSurveyPayload, type CreateSurveyFormValues } from '../schemas/survey.schema';
import { createSurvey, useNpsMe, useNpsUsers } from '../api/use-nps';
import { applyContentI18nUpdate } from '../model/content-i18n';
import { SurveySettingsFields } from '../components/survey/survey-settings-fields';
import { PassbookIdField } from '../components/survey/passbook-id-field';
import { SurveyOwnerSelect } from '../components/survey/survey-owner-select';
import { useNpsAdmin } from '../hooks/use-nps-access';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}

export function SurveyCreateDialog({ open, onOpenChange, onCreated }: Props) {
  const { isAdmin } = useNpsAdmin();
  const { data: me } = useNpsMe({ enabled: open });
  const { data: npsUsers = [] } = useNpsUsers({ enabled: open && isAdmin });
  const [passbookId, setPassbookId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');

  const form = useForm<CreateSurveyFormValues>({
    resolver: zodResolver(createSurveySchema),
    defaultValues: {
      title: '',
      description: '',
      i18n: {},
      response_type: 'ANONYMOUS',
      response_expiration_time: undefined,
      identification_required: false,
      answer_bonus: false,
    },
  });

  useEffect(() => {
    if (!open) return;
    setTargetUserId('');
    setPassbookId('');
  }, [open]);

  useEffect(() => {
    if (!open || isAdmin || targetUserId) return;
    setPassbookId(me?.legacy_user_id ?? '');
  }, [open, isAdmin, targetUserId, me?.legacy_user_id]);

  useEffect(() => {
    if (!open || !isAdmin || !targetUserId) return;
    const target = npsUsers.find((u) => u.id === targetUserId);
    if (target) setPassbookId(target.legacy_user_id ?? '');
  }, [open, isAdmin, targetUserId, npsUsers]);

  const resetLocal = () => {
    form.reset({
      title: '',
      description: '',
      i18n: {},
      response_type: 'ANONYMOUS',
      response_expiration_time: undefined,
      identification_required: false,
      answer_bonus: false,
    });
    setPassbookId('');
    setTargetUserId('');
  };

  const onFinish = form.handleSubmit(async (values) => {
    try {
      if (isAdmin && !targetUserId) {
        toast.error('Selecione o cliente NPS dono da pesquisa.');
        return;
      }

      const nextPassbook = passbookId.trim();

      if (values.answer_bonus && !nextPassbook) {
        toast.warning('Pesquisa bonificada sem Passbook — pontos não serão creditados até vincular o ID.');
      }

      const payload = toSurveyPayload(values);
      const survey = await createSurvey(
        isAdmin && targetUserId ? { ...payload, user_id: targetUserId } : payload,
      );
      toast.success('Pesquisa criada.');
      resetLocal();
      onOpenChange(false);
      onCreated(survey.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar pesquisa.');
    }
  });

  const values = form.watch();
  const responseType = values.response_type ?? 'ANONYMOUS';
  const submitting = form.formState.isSubmitting;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) resetLocal();
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] max-w-xl flex-col overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Nova pesquisa</DialogTitle>
          <DialogDescription>Depois de criar, você adiciona as perguntas e compartilha o link ou o QR Code.</DialogDescription>
        </DialogHeader>

        {/* Um formulário só: as antigas etapas Regras/Destino tinham um campo cada (ou nenhum). */}
        <form onSubmit={onFinish} className="flex min-h-0 flex-1 flex-col gap-4">
          <div className="thin-scrollbar -mx-1 min-h-0 flex-1 space-y-6 overflow-y-auto px-1 pb-1">
            <SurveySettingsFields
              titleValue={values.title}
              descriptionValue={values.description}
              i18nValue={values.i18n}
              titleError={form.formState.errors.title?.message}
              descriptionError={form.formState.errors.description?.message}
              onTitleChange={(v) => form.setValue('title', v, { shouldValidate: true })}
              onDescriptionChange={(v) => form.setValue('description', v, { shouldValidate: true })}
              onI18nChange={(update) => {
                const next = applyContentI18nUpdate(form.getValues('i18n'), update);
                form.setValue('i18n', next, { shouldValidate: true });
              }}
              showBasics
              showExpiration
              showIdentifiedRules
              disabled={submitting}
              values={{
                response_type: responseType,
                identification_required: values.identification_required,
                answer_bonus: values.answer_bonus,
                response_expiration_time: values.response_expiration_time as SurveyExpiration | undefined,
              }}
              onChange={(patch) => {
                Object.entries(patch).forEach(([k, v]) =>
                  form.setValue(k as keyof CreateSurveyFormValues, v as never, { shouldValidate: true }),
                );
              }}
            />

            <section className="space-y-4 border-t border-border/60 pt-5">
              <h3 className="text-sm font-semibold">Destino</h3>
              {isAdmin ? (
                <SurveyOwnerSelect
                  users={npsUsers}
                  value={targetUserId}
                  onChange={setTargetUserId}
                  disabled={submitting}
                />
              ) : null}
              <PassbookIdField
                value={passbookId}
                onChange={setPassbookId}
                disabled
                warnBonusWithoutPassbook={values.answer_bonus}
              />
            </section>
          </div>

          <DialogFooter className="shrink-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                resetLocal();
                onOpenChange(false);
              }}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Criar pesquisa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
